/**
 * Firestore access for Strawberry Patch license reporting.
 * Pass `db` in so this module does not import src/firebase.js (that file calls back here).
 *
 * Orders qualify by customer id (the retail store), never by production facility.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import {
  buildReportRows,
  cleanText,
  designKey,
  isDesignOnlyType,
  normalizeAssociatedInst,
  orderQualifiesForReport,
  validateDesign,
  validateRate
} from './spLicenseReport';

const SETTINGS_DOC = ['spLicenseSettings', 'default'];
const DESIGNS = 'spLicensedDesigns';
const RATES = 'spLicenseRates';

const emptySettings = () => ({
  customerId: '',
  customerName: '',
  retailerName: 'Strawberry Patch',
  clcRetailerCode: '',
  address: '',
  city: '',
  state: '',
  zip: ''
});

export async function getSpLicenseSettings(db) {
  const snap = await getDoc(doc(db, ...SETTINGS_DOC));
  if (!snap.exists()) return emptySettings();
  return { ...emptySettings(), ...snap.data() };
}

export async function saveSpLicenseSettings(db, input) {
  const payload = {
    customerId: input.customerId || '',
    customerName: cleanText(input.customerName),
    retailerName: cleanText(input.retailerName) || 'Strawberry Patch',
    clcRetailerCode: cleanText(input.clcRetailerCode),
    address: cleanText(input.address),
    city: cleanText(input.city),
    state: cleanText(input.state).toUpperCase(),
    zip: cleanText(input.zip),
    updatedAt: serverTimestamp()
  };
  await setDoc(doc(db, ...SETTINGS_DOC), payload, { merge: true });
  return payload;
}

export async function listLicensedDesigns(db) {
  const snap = await getDocs(collection(db, DESIGNS));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.designName || '').localeCompare(String(b.designName || '')));
}

export async function saveLicensedDesign(db, input, id) {
  const error = validateDesign(input);
  if (error) return { ok: false, error };

  const payload = {
    designName: String(input.designName || '').trim(),
    designKey: designKey(input.designName),
    upi: cleanText(input.upi),
    licenseCode: cleanText(input.licenseCode),
    institutionShortCode: cleanText(input.institutionShortCode),
    categorySubCategoryCode: cleanText(input.categorySubCategoryCode),
    prodDescription: cleanText(input.prodDescription),
    associatedInst: normalizeAssociatedInst(input.associatedInst),
    garmentNote: cleanText(input.garmentNote),
    updatedAt: serverTimestamp()
  };

  if (id) {
    await updateDoc(doc(db, DESIGNS, id), payload);
    return { ok: true, id };
  }
  const ref = await addDoc(collection(db, DESIGNS), {
    ...payload,
    createdAt: serverTimestamp()
  });
  return { ok: true, id: ref.id };
}

export async function deleteLicensedDesign(db, id) {
  await deleteDoc(doc(db, DESIGNS, id));
}

export async function listLicenseRates(db) {
  const snap = await getDocs(collection(db, RATES));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.institutionShortCode || '').localeCompare(String(b.institutionShortCode || '')));
}

export async function saveLicenseRate(db, input) {
  const error = validateRate(input);
  if (error) return { ok: false, error };
  const ref = await addDoc(collection(db, RATES), {
    institutionShortCode: cleanText(input.institutionShortCode),
    categoryCode: cleanText(input.categoryCode),
    royaltyPercent: Number(input.royaltyPercent),
    mruAmount: Number(input.mruAmount),
    createdAt: serverTimestamp()
  });
  return { ok: true, id: ref.id };
}

export async function deleteLicenseRate(db, id) {
  await deleteDoc(doc(db, RATES, id));
}

export async function setLineLicenseDesign(db, lineId, designId) {
  await updateDoc(doc(db, 'orderLines', lineId), {
    spLicenseDesignId: designId || null
  });
}

async function designsForKey(db, key) {
  if (!key) return [];
  const snap = await getDocs(query(collection(db, DESIGNS), where('designKey', '==', key)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Block printing a Strawberry Patch retail order when the design has no UPI yet.
 * Other customers, and design-only lines, are ignored.
 */
export async function spPrintBlockReasonForCustomerLines(db, customerId, lines) {
  if (!customerId) return null;
  const settings = await getSpLicenseSettings(db);
  if (!settings.customerId || settings.customerId !== customerId) return null;

  const production = (lines || []).filter((line) => !isDesignOnlyType(line?.type));
  if (production.length === 0) return null;

  const missing = [];
  for (const line of production) {
    const name = String(line?.designName || '').trim();
    const key = designKey(name);
    if (!key) {
      missing.push('a line with no design name');
      continue;
    }
    const matches = await designsForKey(db, key);
    const ready = matches.some((design) => cleanText(design.upi));
    if (!ready) missing.push(name.replace(/^reprint\s*-\s*/i, ''));
  }

  if (missing.length === 0) return null;
  const unique = [...new Set(missing)];
  return `Create a UPI for ${unique.join(', ')} at /splicensereporting before printing this Strawberry Patch design.`;
}

export async function reasonSpLineCannotPrint(db, line) {
  if (!line?.orderId) return null;
  if (isDesignOnlyType(line.type)) return null;
  const orderSnap = await getDoc(doc(db, 'orders', line.orderId));
  if (!orderSnap.exists()) return null;
  return spPrintBlockReasonForCustomerLines(db, orderSnap.data()?.customerId, [line]);
}

export async function loadSpLicenseReport(db, monthKey) {
  const [settings, designs, rates] = await Promise.all([
    getSpLicenseSettings(db),
    listLicensedDesigns(db),
    listLicenseRates(db)
  ]);

  if (!settings.customerId) {
    return { settings, designs, rates, orders: [], rows: [], needsCustomer: true };
  }

  const ordersSnap = await getDocs(
    query(collection(db, 'orders'), where('customerId', '==', settings.customerId))
  );
  const orders = ordersSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((order) => orderQualifiesForReport(order, monthKey));

  const linesByOrderId = {};
  await Promise.all(orders.map(async (order) => {
    const linesSnap = await getDocs(
      query(collection(db, 'orderLines'), where('orderId', '==', order.id))
    );
    linesByOrderId[order.id] = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }));

  const rows = buildReportRows({ orders, linesByOrderId, designs, rates });
  return { settings, designs, rates, orders, rows, needsCustomer: false };
}
